import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { parseFloatToFixed } from "../../../extras";

// Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LabeledIconCard from "../../ui/cards/LabeledIconCard";
import OrderStatsReportService from "../../../api/report/orderStatsReport";
import { formatFinance } from "../../../utils/formatters";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const FinancialReport = ({ startDate = null, endDate = null }) => {
  const theme = useTheme();
  const [data, setData] = useState({
    all_orders_total: 0,
    delivery_total: 0,
    indoor_total: 0,
    takeout_total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Effect to load data when component mounts or dates change
  useEffect(() => {
    loadData();
  }, [loadData, startDate, endDate]);

  // Load data function - using useCallback to prevent unnecessary recreations
  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      // Create params object for API calls
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      // Call the stats API
      const response =
        await OrderStatsReportService.getOrderByTypeFinancialReport(params);

      // Set stats data directly from API response
      setData(response.data);
    } catch (err) {
      console.error("Error loading order stats data:", err);
      setError("Não foi possível carregar os dados de pedidos");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleRefresh = async () => {
    if (refreshing) return;

    try {
    } catch (e) {
      console.error("Error refreshing forecast data:", e);
      setError("Não foi possível atualizar os dados de previsão");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ShowChartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Faturamento
          </Typography>
        </Box>

        <motion.div whileHover="hover" whileTap="tap" variants={buttonVariants}>
          <Tooltip title="Atualizar previsão">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              sx={{
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </motion.div>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {error && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
          }}
        >
          <ErrorOutlineIcon sx={{ mr: 1 }} />
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {loading ? (
          <Box sx={{ display: "flex" }}>
            <Box sx={{ ml: 2 }}>
              <Skeleton
                variant="rectangular"
                width="33%"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ ml: 2 }}>
              <Skeleton
                variant="rectangular"
                width="33%"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ ml: 2 }}>
              <Skeleton
                variant="rectangular"
                width="33%"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ ml: 2 }}>
              <Skeleton
                variant="rectangular"
                width="33%"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <LabeledIconCard
                  icon={<TrendingUpIcon />}
                  title="Faturamento Total"
                  value={formatFinance(
                    parseFloatToFixed(data.all_orders_total, 2)
                  )}
                  color={theme.palette.success.main}
                />
              </motion.div>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <LabeledIconCard
                  icon={<TrendingUpIcon />}
                  title="Faturamento Delivery"
                  value={formatFinance(
                    parseFloatToFixed(data.delivery_total, 2)
                  )}
                  color={theme.palette.success.main}
                />
              </motion.div>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <LabeledIconCard
                  icon={<TrendingUpIcon />}
                  title="Faturamento Retirada"
                  value={formatFinance(
                    parseFloatToFixed(data.takeout_total, 2)
                  )}
                  color={theme.palette.success.main}
                />
              </motion.div>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <LabeledIconCard
                  icon={<TrendingUpIcon />}
                  title="Faturamento Salão"
                  value={formatFinance(parseFloatToFixed(data.indoor_total, 2))}
                  color={theme.palette.success.main}
                />
              </motion.div>
            </Grid>
          </Grid>
        )}
      </motion.div>
    </Box>
  );
};

export default FinancialReport;
